import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuthStore } from '@/state/authStore';

interface Task {
  session_id: string; client_name: string; coach_name: string;
  day_type: string; serial_no: number; reviewer_name: string;
  reviewer_state: string; elapsed_hours: number; sla_flag: string;
}

function SlaIndicator({ flag, hours }: { flag: string; hours: number }) {
  const isEscalation = hours >= 48;
  const style = isEscalation
    ? 'bg-red-100 text-red-800 ring-2 ring-red-400 font-bold'
    : { normal: 'bg-green-50 text-green-700', warning: 'bg-yellow-50 text-yellow-700',
        alert: 'bg-red-50 text-red-700 ring-1 ring-red-300 font-semibold' }[flag] ?? 'bg-gray-50';
  return (
    <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs ${style}`}>
      {Math.round(hours)}h{isEscalation ? ' 🚨' : flag === 'alert' ? ' 🔴' : ''}
    </span>
  );
}

function StateBadge({ state }: { state: string }) {
  const c: Record<string, { label: string; cls: string }> = {
    pending: { label: '未着手', cls: 'bg-orange-100 text-orange-800' },
    in_progress: { label: 'レビュー中', cls: 'bg-blue-100 text-blue-800' },
    released_after_progress: { label: '任意追記可', cls: 'bg-gray-100 text-gray-600' },
  };
  const cfg = c[state] ?? { label: state, cls: 'bg-gray-100 text-gray-600' };
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${cfg.cls}`}>{cfg.label}</span>;
}

type Scope = 'mine' | 'all';

export function DashboardPage() {
  const user = useAuthStore(s => s.currentUser);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL 同期: ?scope=all (admin のみ) / それ以外は mine に正規化
  const rawScope = searchParams.get('scope');
  const scope: Scope = rawScope === 'all' && user?.is_admin ? 'all' : 'mine';

  const setScope = (next: Scope) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'mine') params.delete('scope');
    else params.set('scope', next);
    setSearchParams(params, { replace: true });
  };

  const [required, setRequired] = useState<Task[]>([]);
  const [summary, setSummary] = useState({ pending_count: 0, in_progress_count: 0, alert_count: 0 });
  const [loading, setLoading] = useState(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    api.get(`/dashboard/tasks?scope=${scope}`).then(r => {
      if (reqId !== requestIdRef.current) return; // stale response guard
      setRequired(r.data.required);
      setSummary(r.data.summary);
      setLoading(false);
    }).catch(() => {
      if (reqId !== requestIdRef.current) return;
      setLoading(false);
    });
  }, [scope]);

  const isAll = scope === 'all';

  return (
    <div>
      <h1 className="text-xl font-semibold text-gray-900">タスクダッシュボード</h1>
      <p className="mt-1 text-sm text-gray-500">
        {isAll ? '全 reviewer のタスク進捗（管理者ビュー）' : '自分が担当する reviewer タスク'}
      </p>

      {user?.is_admin && (
        <div className="mt-4 flex gap-1 border-b border-gray-200">
          {([
            { key: 'mine', label: '自分のタスク' },
            { key: 'all', label: '全体' },
          ] as const).map(t => (
            <button
              key={t.key}
              onClick={() => setScope(t.key)}
              className={[
                'px-4 py-2 text-sm font-medium -mb-px border-b-2 transition',
                scope === t.key
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700',
              ].join(' ')}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="mt-8 p-8 text-center text-gray-400">読み込み中...</div>
      ) : (
        <>
      <div className="mt-4 grid grid-cols-3 gap-4">
        <div className="rounded-lg bg-white p-4 shadow-sm"><p className="text-3xl font-bold text-orange-600">{summary.pending_count}</p><p className="text-sm text-gray-500">未着手</p></div>
        <div className="rounded-lg bg-white p-4 shadow-sm"><p className="text-3xl font-bold text-blue-600">{summary.in_progress_count}</p><p className="text-sm text-gray-500">レビュー中</p></div>
        <div className="rounded-lg bg-white p-4 shadow-sm"><p className="text-3xl font-bold text-red-600">{summary.alert_count}</p><p className="text-sm text-gray-500">対応遅延（24h超）</p></div>
      </div>

      <div className="mt-6">
        <h2 className="text-sm font-semibold text-gray-700">
          {isAll ? '全タスク' : '必須タスク'}（{required.length}件）
        </h2>
        <div className="mt-2 space-y-2">
          {required.length === 0 ? (
            <div className="rounded-lg bg-white p-6 text-center text-sm text-gray-400 shadow-sm">対応が必要なタスクはありません</div>
          ) : required.map((t, i) => (
            <div key={i} onClick={() => navigate(`/sessions/${t.session_id}/review`)}
              className="flex cursor-pointer items-center justify-between rounded-lg bg-white p-4 shadow-sm hover:bg-gray-50">
              <div><p className="text-sm font-medium text-gray-900">{t.coach_name} / {t.client_name}</p><p className="text-xs text-gray-500">{t.day_type} #{t.serial_no} — {t.reviewer_name}</p></div>
              <div className="flex items-center gap-3"><StateBadge state={t.reviewer_state} /><SlaIndicator flag={t.sla_flag} hours={t.elapsed_hours} /></div>
            </div>
          ))}
        </div>
      </div>
        </>
      )}

    </div>
  );
}
